import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { aboutData } from '../data/mock';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AboutPage = () => {
  const [portraitUrl, setPortraitUrl] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings`);
      if (res.ok) {
        const data = await res.json();
        setPortraitUrl(data.about_portrait_url || '');
      }
    } catch (e) {
      console.error('Error fetching settings:', e);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="About Hassaan Khan - Founder & Creative Director"
        description="Hassaan Khan is the founder of CreativeDirectorKhan | KHAN, with over a decade of experience in film production, digital marketing, branding, and web development. Serving clients worldwide."
        keywords="Hassaan Khan, creative director, cinematographer, founder, filmmaker, digital strategist, brand developer, worldwide"
        canonical="https://creativedirectorkhan.com/about"
      />
      
      {/* Hero Header with Portrait */}
      <section className="pt-32 pb-20 px-8 md:px-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-white/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-end">
            <div>
              <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">About</p>
              <h1 className="font-serif text-6xl md:text-8xl lg:text-[120px] font-light text-white leading-[0.85]">
                Hassaan
                <span className="block text-white/40 italic">Khan</span>
              </h1>
              <p className="text-white/50 text-lg mt-8 tracking-wide">
                Founder & Creative Director — CreativeDirectorKhan | KHAN
              </p>
            </div>
            
            {/* Portrait Image */}
            {portraitUrl && (
              <div className="relative">
                <div className="aspect-[3/4] max-w-md ml-auto overflow-hidden">
                  <img 
                    src={portraitUrl} 
                    alt="Hassaan Khan - Creative Director"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-full h-full border border-white/10 -z-10" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-px bg-white/30" />
                <span className="text-white/40 text-xs tracking-[0.3em] uppercase">Introduction</span>
              </div>
              <p className="font-serif text-3xl md:text-4xl font-light text-white/90 leading-relaxed">
                {aboutData.intro}
              </p>
            </div>
            <div className="space-y-8 lg:pt-16">
              <p className="text-white/60 text-lg leading-relaxed">
                {aboutData.philosophy}
              </p>
              <p className="text-white/60 leading-relaxed">
                {aboutData.approach}
              </p>
              <p className="text-white/50 leading-relaxed">
                Every project is approached with clarity, intention, and craftsmanship. By combining strategic thinking with cinematic storytelling and digital expertise, KHAN helps businesses communicate with purpose and stand out in competitive markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="flex items-center gap-4 mb-16">
            <div className="w-12 h-px bg-white/30" />
            <span className="text-white/40 text-xs tracking-[0.3em] uppercase">Values</span>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {[
              { title: 'Precision', desc: 'Every detail matters. I approach each project with meticulous attention to craft and quality.' },
              { title: 'Collaboration', desc: 'Great work comes from genuine partnership. Your vision guides the creative journey.' },
              { title: 'Excellence', desc: 'Good enough never is. I push boundaries to deliver exceptional, lasting results.' }
            ].map((value, index) => (
              <div key={index} className="group">
                <span className="font-serif text-6xl text-white/[0.08] group-hover:text-white/[0.15] transition-colors duration-500 block mb-4">
                  0{index + 1}
                </span>
                <h3 className="font-serif text-2xl font-light text-white/90 mb-4">
                  {value.title}
                </h3>
                <p className="text-white/40 leading-relaxed">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-8 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white mb-8">
            Let&apos;s Work
            <span className="italic text-white/40"> Together</span>
          </h2>
          <p className="text-white/40 mb-14 max-w-md mx-auto">
            I&apos;m always interested in hearing about new projects and opportunities.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-4 px-12 py-5 border border-white/20 text-white/90 text-sm tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500 group"
          >
            <span>Get in Touch</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
